import { requestLimitedTimeOfferOnEditorExit } from '@/components/ShowModal/LimitedTimeOfferLifecycle'
import { useEffect, useMemo, useState } from 'react'
import { ThemeProvider } from 'baseui'
import { lightTheme } from '../../../../customTheme'
import useAppContext from '@/hooks/useAppContext'
import { useAppDispatch } from '@/store/store'
import { Z_INDEX } from '@/constants/z-index'
import { SignInManager } from './components/SignInManager'
import { selectOpenModalTryPremium, selectUser, selectUserIsPremium } from '@/store/slices/user/selectors'
import { useSelector } from 'react-redux'
import { selectDisableEditorState, selectEditorState } from '@/store/slices/editor/selector'
import { useHistory, useLocation } from 'react-router-dom'
import { getNavbarConfig, getNavbarPremiumSource, NavbarMode } from './navbarConfig'
import { renderNavbarButton, NavbarButtonContext } from './NavbarButtonRenderer'
import { useEditorContext } from '@/scenes/engine'
import { selectImageElements } from '@/store/slices/imageElement/selectors'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { ActionType } from '@/store/slices/editor/reducer'
import { SAVE_PROJECT_STATE, ShowModalType } from '@/scenes/engine/common/constants'
// `MediaImageFallbackHandler` is only used by the editor route. Static imports
// of this module would pull `fabric`, `CanvasImageRenderer`, and the media
// repository chain into every page that renders the global Navbar (every
// `/create/*` page, dashboards, etc.). It is dynamically imported inside the
// editor-guarded setup effect below.
import * as amplitude from '@amplitude/analytics-browser'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { UserInfoStorage } from '@/utils/userInfoStorage'
import { initializeProfitWell } from '@/libs/profitwell'
import { syncOneSignalUser } from '@/libs/onesignal'
import { setShowModalCustomText } from '@/store/slices/modal/customTextModal'
import { selectPhotoshootIsGenerating, selectPhotoshootResults } from '@/store/slices/photoshoot/selectors'
import { requestPhotoshootDiscard } from '@/store/slices/photoshoot/actions'
import {
  selectAIFashionDisableDownloadAll,
  selectAIFashionIsLoading,
  selectAIFashionResults,
} from '@/store/slices/aiFashion/selectors'
import { NavbarShell } from '@/components/NavbarShell'
import { isPartnerRoute } from '@/constants/routes'
import { getGeneratedAIFashionResultDataUrls } from '@/scenes/AIStudioLite/utils/resultImages'
import { useCreateCtaButtonExperiment } from '@/hooks/useCreateCtaButtonExperiment'

function NavbarEditor() {
  const { isOpenPixelManipulationObject, setSaveProjectState } = useAppContext()
  const disableEditorState = useSelector(selectDisableEditorState)
  const editorState = useSelector(selectEditorState)
  const location = useLocation()
  const history = useHistory()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isAIStudioRoute =
    location.pathname.startsWith('/ai-studio') &&
    new URLSearchParams(location.search).get('fullEditor') !== '1'
  // Batch studio renders its own navbar (BatchStudioNavbar), so hide the global one.
  const isBatchStudioRoute = location.pathname.startsWith('/batch-studio')
  // Partner landing pages render their own branded header.
  const isPartnerLanding = isPartnerRoute(location.pathname)

  const editor = useEditorContext().editor
  const filterPacks = useSelector(selectImageElements)

  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const user = useSelector(selectUser)
  const userIsPremium = useSelector(selectUserIsPremium)
  const openModalTryPremium = useSelector(selectOpenModalTryPremium)

  const createCtaButtonId = useCreateCtaButtonExperiment()

  // User premium state effect
  useEffect(() => {
    if (user) {
      setIsLoggedIn(true)
      if (openModalTryPremium.source) {
        if (userIsPremium) {
          // no-op
        } else if (!openModalTryPremium.opened) {
          dispatch(
            setOpenModalTryPremium({
              ...openModalTryPremium,
              isOpen: true,
            })
          )
        }
      }
    } else {
      setIsLoggedIn(false)
    }
  }, [user, userIsPremium])


  
  // Initialization effect
  useEffect(() => {
    function getUser(): boolean {
      let userInfo = UserInfoStorage.get()
      if (userInfo) {
        SignInManager.getInstance().setUserProperties(userInfo)
        SignInManager.getInstance().refreshTransactionStatus()

        setIsLoggedIn(true)

        const identifyEvent = new amplitude.Identify()
        identifyEvent.set('isSignup', true)
        amplitude.identify(identifyEvent)
        amplitude.setUserId(userInfo.userId)

        Sentry.setUser({ id: userInfo.userId })
        initializeProfitWell(userInfo.userId)
        syncOneSignalUser({ id: userInfo.userId })
        return true
      } else {
        const identifyEvent = new amplitude.Identify()
        identifyEvent.set('isSignup', false)
        amplitude.identify(identifyEvent)
        Sentry.setUser(null)
        // Also detaches a user the SDK persisted from an earlier session (e.g.
        // after a forced logout); a no-op for never-identified visitors.
        syncOneSignalUser(null)
        return false
      }
    }
    getUser()
  }, [])

  // History blocking
  useEffect(() => {
    if (!editor) return
    const unblock = history.block((location, action) => {
      if (!editor.handlers.transactionHandler.isFlushed) {
        if (action === 'POP') {
          const confirmNavigation = window.confirm(t('You have unsaved changes. Are you sure you want to leave?'));
          if (!confirmNavigation) {
            history.go(1)
            return false
          } else {
            setSaveProjectState(SAVE_PROJECT_STATE.END)
            editor.handlers.transactionHandler.clear()
          }
        }
      }
    });

    return () => {
      unblock();
    };
  }, [history, editor, setSaveProjectState, t]);

  // Media fallback handler setup. Only the editor mounts the canvas the
  // fallback handler operates on; on every other route `editor` is null and
  // the effect bails out before pulling the heavy module.
  useEffect(() => {
    if (!editor) return
    let cancelled = false
    const sizeProvider = () => editor.handlers.frameHandler.getSize();
    const filtersProvider = () => filterPacks

    void import('@/scenes/engine/objects/media-repository/media_image_fallback_handler')
      .then(({ MediaImageFallbackHandler }) => {
        if (cancelled) return
        MediaImageFallbackHandler.getInstance().setup(
          editor.handlers.canvasHandler.canvas,
          sizeProvider,
          filtersProvider,
        )
      })
    return () => {
      cancelled = true
    }
  }, [editor, filterPacks]);

  // Photoshoot run-step state lives outside the editor (the route is canvas-less),
  // so the Navbar reads results / the discard signal through Redux instead of
  // the old `editor.handlers.photoshootHandler` singleton. A single subscription
  // to `selectPhotoshootResults` is paired with a `useMemo` to derive both the
  // ready array and the boolean flag — halving the store subscriptions and
  // keeping the derived values in lockstep.
  const photoshootResults = useSelector(selectPhotoshootResults)
  const photoshootIsGenerating = useSelector(selectPhotoshootIsGenerating)
  const aiFashionResults = useSelector(selectAIFashionResults)
  const aiFashionIsLoading = useSelector(selectAIFashionIsLoading)
  const aiFashionDisableDownloadAll = useSelector(selectAIFashionDisableDownloadAll)
  const { photoshootReadyResults, photoshootHasResults } = useMemo(() => {
    const readyResults = photoshootResults.filter((r): r is string => Boolean(r))
    return {
      photoshootReadyResults: readyResults,
      photoshootHasResults: readyResults.length > 0,
    }
  }, [photoshootResults])

  // The photoshoot run step is the only `/create/*` page that opts into the
  // navbar's `customButtonSet` mode (back / tryPremium / downloadAll). We
  // gate the override on `navbarButtonSet === 'photoshoot'` so leftover
  // editor-side `customButtonSet` state (e.g. from AI Fashion) cannot leak
  // into other `/create/*` routes.
  const navbarMode: NavbarMode =
    editorState?.navbar === 'customButtonSet' && editorState?.navbarButtonSet === 'photoshoot'
      ? 'customButtonSet'
      : location.pathname.includes('create')
        ? 'create'
        : editorState?.navbar ?? 'editor'

  const navbarCallbacks = useMemo(() => {
    if (editorState?.navbarButtonSet === 'aiFashion') {
      return {
        downloadAll: () => getGeneratedAIFashionResultDataUrls(aiFashionResults),
      }
    }
    if (editorState?.navbarButtonSet === 'photoshoot') {
      return {
        downloadAll: () => {
          // @ts-ignore
          window.dataLayer.push({ event: 'photoshoot_download' })
          return photoshootReadyResults
        },
        onSignOut: () => {
          dispatch(requestPhotoshootDiscard())
        },
      }
    }
    return undefined
  }, [aiFashionResults, dispatch, editorState?.navbarButtonSet, photoshootReadyResults])

  const getDownloadAllSource = (navbarButtonSet: string | undefined): string | undefined => {
    const isRendered = [...config.left, ...config.right].includes('downloadAll')
    if (!isRendered) return undefined

    if (navbarButtonSet === 'photoshoot') return 'Photoshoot'
    if (navbarButtonSet === 'aiFashion') return 'AI_models'
    return undefined
  }

  const getDisableDownloadAll = (navbarButtonSet: string | undefined): boolean => {
    const isRendered = [...config.left, ...config.right].includes('downloadAll')
    if (!isRendered) return false

    if (navbarButtonSet === 'photoshoot') return !photoshootHasResults
    return navbarButtonSet === 'aiFashion' && aiFashionDisableDownloadAll
  }

  const config = getNavbarConfig(navbarMode, editorState?.navbarButtonSet, navbarCallbacks)

  // Only the create-page CTA is subject to the experiment; every other
  // navbar mode keeps the config's baseline button list untouched.
  const configRight =
    navbarMode === 'create'
      ? config.right.map(id => (id === 'tryPremium' ? createCtaButtonId : id))
      : config.right

  const onBack = () => {
    const mode = editorState.floatingInspectorButtonSet
    if (mode === 'aiFashion') {
      setShowModalCustomText(t('Discard project?'), t('Your project will not be saved.'), t('Discard'), t('Cancel'), true, () => { requestLimitedTimeOfferOnEditorExit(); history.push('/create/ai-models') })
    } else if (mode === 'photoshoot') {
      // The run step lives at the same route, so we signal the route-local
      // component via Redux instead of pushing history. The run component
      // listens for `discardRequested`, returns to its form step, and clears
      // the signal.
      if (!photoshootHasResults) {
        dispatch(requestPhotoshootDiscard())
        return
      }
      setShowModalCustomText(
        t('Discard results?'),
        t('They will not be saved.'),
        t('Discard'),
        t('Cancel'),
        true,
        () => dispatch(requestPhotoshootDiscard()),
      )
    }
  }

  // The Settings button mirrors `disableNavbar`, but the AI Model and
  // Photoshoot product navbars also disable it while their generation is
  // loading (matching the existing Download All disabling behavior). The
  // `Try Remove objects` row is hidden across all AI Model / Photoshoot
  // surfaces (dashboard list pages + their run navbars); the editor and
  // other create pages keep it.
  //
  // `editorState.navbarButtonSet === 'aiFashion'` can persist after the
  // user backs out of the AI Fashion editor (no Editor-unmount reset), so
  // gate the product flags on `navbarMode === 'customButtonSet'` — the same
  // gate `navbarMode` already uses for photoshoot. This matches the rule
  // that a leaked `navbarButtonSet` must not leak product behavior into
  // unrelated `/create/*` routes. The route-based flags below cover the
  // dashboard pages where the navbar is in `'create'` mode.
  const isAIFashionProduct =
    navbarMode === 'customButtonSet' && editorState?.navbarButtonSet === 'aiFashion'
  const isPhotoshootProduct =
    navbarMode === 'customButtonSet' && editorState?.navbarButtonSet === 'photoshoot'
  const isAIModelRoute = location.pathname.includes('/create/ai-models')
  const isPhotoshootRoute = location.pathname.includes('/create/photoshoots')
  const hideTryRemoveObjects =
    isAIFashionProduct || isPhotoshootProduct || isAIModelRoute || isPhotoshootRoute
  const isProductLoading =
    (isAIFashionProduct && aiFashionIsLoading) ||
    (isPhotoshootProduct && photoshootIsGenerating)

  const ctx: NavbarButtonContext = {
    isLoggedIn,
    isOpenPixelManipulationObject,
    disableNavbar: disableEditorState.disableNavbar,
    disableDownloadAll: getDisableDownloadAll(editorState?.navbarButtonSet),
    disableUserMenu: disableEditorState.disableNavbar || isProductLoading,
    hideTryRemoveObjects,
    premiumSource: getNavbarPremiumSource(navbarMode, editorState?.navbarButtonSet),
    mode: editorState?.floatingInspectorButtonSet,
    onBack,
    callbacks: config.callbacks,
    downloadAllDestination: getDownloadAllSource(editorState?.navbarButtonSet),
    downloadAllSource: getDownloadAllSource(editorState?.navbarButtonSet),
  }

  if (isAIStudioRoute || isBatchStudioRoute || isPartnerLanding) return null

  return (
    <NavbarShell
      left={config.left.map(id => renderNavbarButton(id, ctx))}
      right={configRight.map(id => renderNavbarButton(id, ctx))}
      style={{
        opacity: 1,
        pointerEvents: disableEditorState.disableNavbar ? 'none' : 'auto',
        ...config.style,
      }}
    />
  )
}

export default NavbarEditor
